import type { Board, Guest, Item } from '../types/board';

const API_URL = import.meta.env.VITE_API_URL || 'https://moodboard-api.priyabagus-a29.workers.dev';
const UPLOAD_URL = import.meta.env.VITE_UPLOAD_URL || 'https://upload.mystaline.dev';

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return res.json() as Promise<T>;
}

export function money(n: number) {
  return `Rp ${Math.round(n || 0).toLocaleString('id-ID')}`;
}

export function paidOf(item: Pick<Item, 'payments'>) {
  return (item.payments || []).reduce((a, p) => a + (p.amount || 0), 0);
}

export function createBoard() {
  return fetch(`${API_URL}/planner-boards`, { method: 'POST' }).then((r) =>
    json<{ boardId: string; editToken: string; editUrl: string; checkinUrl: string; board: Board; updatedAt: number }>(r),
  );
}

export function getBoard(editToken: string) {
  return fetch(`${API_URL}/planner-boards/edit/${encodeURIComponent(editToken)}`).then((r) =>
    json<{ boardId: string; board: Board; updatedAt: number }>(r),
  );
}

export function updateBoard(editToken: string, board: Board, updatedAt: number) {
  return fetch(`${API_URL}/planner-boards/edit/${encodeURIComponent(editToken)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ board, updatedAt }),
  }).then((r) => {
    if (r.status === 409) return json<{ error: 'conflict'; board: Board; latestUpdatedAt: number }>(r).then((c) => {
      throw Object.assign(new Error('conflict'), { conflict: c });
    });
    return json<{ boardId: string; board: Board; updatedAt: number }>(r);
  });
}

export function getCheckinShareUrl(editToken: string) {
  return fetch(`${API_URL}/planner-boards/edit/${encodeURIComponent(editToken)}/checkin-share`).then((r) =>
    json<{ checkinUrl: string }>(r),
  );
}

export function getCheckinGuests(checkinToken: string) {
  return fetch(`${API_URL}/planner-boards/checkin/${encodeURIComponent(checkinToken)}`).then((r) =>
    json<{ guests: Guest[] }>(r),
  );
}

export function setGuestCheckedIn(checkinToken: string, guestId: string, checkedIn: boolean) {
  return fetch(`${API_URL}/planner-boards/checkin/${encodeURIComponent(checkinToken)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ guestId, checkedIn }),
  }).then((r) => json<{ guests: Guest[] }>(r));
}

export function uploadImage(file: File) {
  const form = new FormData();
  form.append('file', file);
  return fetch(`${UPLOAD_URL}/upload`, { method: 'POST', body: form }).then((r) =>
    json<{ key: string; url: string; contentType: string }>(r),
  );
}

// Delete an uploaded image. Requires the board edit token (owner-only).
export function deleteImage(key: string, editToken: string) {
  return fetch(
    `${UPLOAD_URL}/upload?key=${encodeURIComponent(key)}&token=${encodeURIComponent(editToken)}`,
    { method: 'DELETE' },
  ).then((r) => json<{ ok: boolean }>(r));
}

// R2 key for an uploaded image URL, or null when the URL isn't a CDN upload.
export function cdnKey(url: string): string | null {
  const base = 'https://cdn.mystaline.dev/';
  return url.startsWith(base) ? url.slice(base.length) : null;
}
